import React from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FormikProps } from "formik";
import { MyNewsFilterBody } from "my-news/components";
import { OcDatePicker, OcTimePicker } from "oc/components";
import { getDateFormatByLocale } from "oc/utils";

// Define the shape of the form values used by this component
interface NewsItemFormValues {
  validFrom: Date | string | null;
  validTo: Date | string | null;
  isEdit: boolean;
  // ... egyéb mezők, ha szükségesek
}

interface MyNewsItemFormFilterValidityProps {
  formik: FormikProps<NewsItemFormValues>;
  open: boolean;
  isMobile: boolean;
  published: boolean;
}

export const MyNewsItemFormFilterValidity: React.FC<
  MyNewsItemFormFilterValidityProps
> = ({ formik, open, isMobile, published }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  // A minimum dátum a validFrom vagy a mostani dátum
  let minDate = new Date();
  if (formik.values.validFrom) {
    minDate = new Date(formik.values.validFrom);
  }

  const getDateValue = (
    date: Date | string | null | undefined,
  ): Date | null => {
    return date ? new Date(date) : null;
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const updatedDate = new Date(date);
      const currentTime = new Date();
      updatedDate.setHours(currentTime.getHours());
      updatedDate.setMinutes(currentTime.getMinutes());
      updatedDate.setSeconds(currentTime.getSeconds());
      updatedDate.setMilliseconds(0);
      formik.setFieldValue("validFrom", updatedDate);
    } else {
      formik.setFieldValue("validFrom", null);
    }
  };

  const handleTimeChange = (time: Date | null) => {
    if (formik.values.validFrom && time) {
      const updatedDate = new Date(formik.values.validFrom);
      updatedDate.setHours(time.getHours());
      updatedDate.setMinutes(time.getMinutes());
      updatedDate.setSeconds(time.getSeconds());
      updatedDate.setMilliseconds(0);
      formik.setFieldValue("validFrom", updatedDate);
    }
  };

  const handleDateToChange = (date: Date | null) => {
    if (date) {
      const updatedDate = new Date(date);
      const currentTime = new Date();
      updatedDate.setHours(currentTime.getHours());
      updatedDate.setMinutes(currentTime.getMinutes());
      updatedDate.setSeconds(currentTime.getSeconds());
      updatedDate.setMilliseconds(0);
      formik.setFieldValue("validTo", updatedDate);
    } else {
      formik.setFieldValue("validTo", null);
    }
  };

  const handleTimeToChange = (time: Date | null) => {
    if (formik.values.validTo && time) {
      const updatedDate = new Date(formik.values.validTo);
      updatedDate.setHours(time.getHours());
      updatedDate.setMinutes(time.getMinutes());
      updatedDate.setSeconds(time.getSeconds());
      updatedDate.setMilliseconds(0);
      formik.setFieldValue("validTo", updatedDate);
    }
  };

  return (
    <MyNewsFilterBody
      open={open}
      type="validity"
      width={isMobile ? "auto" : "568px"}
    >
      <Box
        padding="16px"
        style={{ backgroundColor: theme.palette.gray20.main }}
      >
        {/* Érvényesség kezdete */}
        <Box
          display="flex"
          justifyContent="space-between"
          flexDirection="row"
          alignItems="center"
        >
          <Box
            padding={0}
            style={{
              backgroundColor: theme.palette.white.main,
              width: "calc(50% - 8px)",
            }}
          >
            <OcDatePicker
              error={
                formik.touched.validFrom && Boolean(formik.errors.validFrom)
              }
              helperText={
                (formik.touched.validFrom && formik.errors.validFrom) || " "
              }
              id="validFrom"
              //name="validFrom"
              label={t("MY_NEWS_START_DATE")}
              minDate={minDate}
              //minDateMessage={t("OC_NOT_SELECTABLE")}
              onChange={handleDateChange}
              value={getDateValue(formik.values.validFrom)}
              format={getDateFormatByLocale(i18n.language)}
              disabled={formik.values.isEdit && published}
              //onFocus={() => formik.setTouched({}, false)}
            />
          </Box>
          <Box
            style={{
              backgroundColor: theme.palette.white.main,
              width: "calc(50% - 8px)",
            }}
          >
            <OcTimePicker
              error={
                formik.touched.validFrom && Boolean(formik.errors.validFrom)
              }
              helperText={
                (formik.touched.validFrom && formik.errors.validFrom) || " "
              }
              label={t("OC_START_DATE")}
              id="validFrom"
              //name="validFrom"
              value={getDateValue(formik.values.validFrom)}
              onChange={handleTimeChange}
              fullWidth
              onFocus={() => formik.setTouched({}, false)}
              disabled={formik.values.isEdit && published}
            />
          </Box>
        </Box>

        <Box height="36px" />

        {/* Érvényesség vége */}
        <Box
          display="flex"
          justifyContent="space-between"
          flexDirection="row"
          alignItems="center"
        >
          <Box
            padding={0}
            style={{
              backgroundColor: theme.palette.white.main,
              width: "calc(50% - 8px)",
            }}
          >
            <OcDatePicker
              error={formik.touched.validTo && Boolean(formik.errors.validTo)}
              helperText={
                (formik.touched.validTo && formik.errors.validTo) ||
                t("OC_OPTIONAL")
              }
              id="validTo"
              label={t("MY_NEWS_END_DATE")}
              minDate={minDate}
              //minDateMessage={t("OC_NOT_SELECTABLE")}
              onChange={handleDateToChange}
              value={getDateValue(formik.values.validTo)}
              format={getDateFormatByLocale(i18n.language)}
              //onFocus={() => formik.setTouched({}, false)}
              disabled={!formik.values.validFrom}
            />
          </Box>
          <Box
            style={{
              backgroundColor: theme.palette.white.main,
              width: "calc(50% - 8px)",
            }}
          >
            <OcTimePicker
              error={formik.touched.validTo && Boolean(formik.errors.validTo)}
              helperText={
                (formik.touched.validTo && formik.errors.validTo) ||
                t("OC_OPTIONAL")
              }
              label={t("OC_END_DATE")}
              id="validTo"
              //name="validTo"
              value={getDateValue(formik.values.validTo)}
              onChange={handleTimeToChange}
              fullWidth
              onFocus={() => formik.setTouched({}, false)}
              disabled={!formik.values.validFrom}
            />
          </Box>
        </Box>

        <Box height="20px" />
      </Box>
    </MyNewsFilterBody>
  );
};

export default MyNewsItemFormFilterValidity;
